import React, { useState } from 'react';
import Navigation from '../Navigation';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { 
  Mail,
  Brain,
  Clock,
  TrendingUp,
  Shield,
  Eye,
  CheckCircle,
  ArrowRight,
  Lightbulb,
  Target,
  Users,
  MessageCircle,
  Star,
  Zap,
  Lock,
} from 'lucide-react';

const VioCoachPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setSubmitMessage('Please enter your email address.');
      setSubmitSuccess(false);
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${BACKEND_URL}/api/email-signup/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          source: 'viocoach'
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage(data.message || 'Thank you! You\'ll be the first to know when VioCoach launches.');
        setSubmitSuccess(true);
        setEmail('');
      } else {
        setSubmitMessage(data.detail || 'An error occurred. Please try again.');
        setSubmitSuccess(false);
      }
    } catch (error) {
      console.error('Email signup error:', error);
      setSubmitMessage('Network error. Please check your connection and try again.');
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const keyBenefits = [
    {
      icon: Clock,
      title: 'On-Demand Coaching',
      description: 'No scheduling required.',
      benefit: 'Get guidance whenever you need it, without waiting for appointments.'
    },
    {
      icon: TrendingUp,
      title: 'Leadership Development',
      description: 'Build strategic thinking and confidence.',
      benefit: 'Enhance your executive presence and decision-making skills.'
    },
    {
      icon: Shield,
      title: 'Burnout Support',
      description: 'Address stress before it becomes a problem.',
      benefit: 'Maintain peak performance while protecting your wellbeing.'
    },
    {
      icon: Lock,
      title: 'Private & Secure',
      description: 'Your thoughts stay confidential and offline with enterprise-grade security.',
      benefit: 'GDPR and SOC2 compliant platform ensures maximum data protection and privacy.'
    },
    {
      icon: Brain,
      title: 'Executive Insights',
      description: 'Understand patterns, blind spots, and strengths.',
      benefit: 'Gain self-awareness that accelerates your leadership growth.'
    },
    {
      icon: Zap,
      title: 'AI-Powered Intelligence',
      description: 'Advanced AI adapts to your unique leadership style and challenges.',
      benefit: 'Personalized coaching recommendations based on proven methodologies.'
    }
  ];

  const coachingAreas = [
    {
      icon: Target,
      title: 'Strategic Decision Making',
      description: 'Navigate complex business decisions with confidence and clarity.',
      benefits: ['Framework for complex problems', 'Risk assessment guidance', 'Stakeholder impact analysis']
    },
    {
      icon: Users,
      title: 'Team Leadership',
      description: 'Build and inspire high-performing teams that deliver results.',
      benefits: ['Communication strategies', 'Conflict resolution techniques', 'Performance management']
    },
    {
      icon: MessageCircle,
      title: 'Executive Presence',
      description: 'Command respect and influence in every room you enter.',
      benefits: ['Communication mastery', 'Confidence building', 'Authority development']
    },
    {
      icon: Lightbulb,
      title: 'Innovation & Growth',
      description: 'Drive organizational change and foster innovative thinking.',
      benefits: ['Change management', 'Innovation frameworks', 'Growth strategies']
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-12 sm:py-16 lg:py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_react-quiz-portal/artifacts/8lc0733a_VioCoach.png" 
            alt="VioCoach - Executive AI Voice Coach"
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#112d56]/80 via-[#1a3d6b]/70 to-[#112d56]/80"></div>
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8 animate-fade-in text-center lg:text-left">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 bg-[#ff7a1f]/20 backdrop-blur-sm rounded-full px-3 sm:px-4 py-2 mb-4">
                  <Star className="h-4 w-4 sm:h-5 sm:w-5 text-[#ff7a1f]" />
                  <span className="text-[#ff7a1f] font-semibold text-sm sm:text-base">COMING VERY SOON</span>
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold leading-tight tracking-tight">
                  Executive coaching. <span className="text-[#ff7a1f]">Reimagined</span>.
                </h1>
                <p className="text-lg sm:text-xl lg:text-2xl text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                  AI-powered voice coaching for managers and C-suite leaders — available anytime.
                </p>
              </div>
              
              <div className="flex flex-col gap-4">
                <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    placeholder="Enter your email for launch updates"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 px-4 sm:px-6 py-3 rounded-xl text-[#112d56] placeholder-gray-500 font-medium min-w-0"
                    required
                  />
                  <Button 
                    type="submit"
                    className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-semibold px-4 sm:px-6 py-3 rounded-xl transition-all duration-300 whitespace-nowrap"
                  >
                    <Mail className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    Get Launch Updates
                  </Button>
                </form>
              </div>
            </div>
            
            {/* Right side - now empty since image is background */}
            <div className="lg:text-right hidden lg:block">
              {/* This space can be used for additional content or left empty */}
            </div>
          </div>
        </div>
      </section>

      {/* What is VioCoach Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="space-y-6 mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                What is <span className="text-[#ff7a1f]">VioCoach</span>?
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                VioCoach is a private, voice-based executive coach designed to support ambitious managers, team leads, and executives. Delivered through natural, real-time voice conversations, VioCoach helps leaders reflect, plan, and grow — without waiting for the next 1:1 or performance review.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#112d56] mb-4 sm:mb-6">
              Key <span className="text-[#ff7a1f]">Benefits</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Unlock your leadership potential with AI-powered coaching that's always available.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {keyBenefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <CardHeader className="text-center pb-2">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="h-6 w-6 sm:h-8 sm:w-8 text-[#ff7a1f]" />
                  </div>
                  <CardTitle className="text-lg sm:text-xl font-bold text-[#112d56] px-2">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-center space-y-3 px-4">
                  <CardDescription className="text-gray-600 text-sm sm:text-base leading-relaxed">
                    {benefit.description}
                  </CardDescription>
                  <p className="text-xs sm:text-sm text-[#ff7a1f] font-medium italic">
                    {benefit.benefit}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Coaching Areas Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
              Leadership <span className="text-[#ff7a1f]">Development Areas</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              VioCoach helps leaders stay sharp, aligned, and emotionally resilient in fast-moving environments. It's like having a high-performance coach on call — ready to guide you, challenge you, and grow with you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {coachingAreas.map((area, index) => (
              <Card key={index} className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="h-3 bg-gradient-to-r from-[#ff7a1f] to-[#ff7a1f]/70"></div>
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-3">
                    <area.icon className="h-6 w-6 text-[#ff7a1f]" />
                    <CardTitle className="text-xl font-bold text-[#112d56]">{area.title}</CardTitle>
                  </div>
                  <CardDescription className="text-gray-600 text-base leading-relaxed">
                    {area.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {area.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="h-4 w-4 text-[#ff7a1f] mt-1 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* VioCoach Methodology Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                The VioCoach <span className="text-[#ff7a1f]">Methodology</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                A Fusion of Proven Coaching Models for Lasting Transformation
              </p>
            </div>

            <div className="space-y-12">
              {/* Introduction */}
              <div className="max-w-5xl mx-auto text-center">
                <Card className="border-0 shadow-xl bg-white">
                  <CardContent className="p-10">
                    <p className="text-lg text-gray-700 leading-relaxed mb-6">
                      At the heart of VioCoach lies a multi-model coaching framework that combines the most respected and effective coaching methodologies in the world. Each model plays a unique role, but together they create a comprehensive, flexible, and deeply human approach to leadership and personal development.
                    </p>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      These six models are not just tools — they are integrated coaching pathways designed to unlock potential, build emotional intelligence, sharpen decision-making, and fuel sustainable behaviour change. From goal-driven planning to values-based transformation, VioCoach seamlessly integrates multiple proven methodologies into one intelligent coaching experience.
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Methodology Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <div className="h-3 bg-gradient-to-r from-blue-500 to-blue-600"></div>
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-3">
                      <Target className="h-6 w-6 text-blue-500" />
                      <CardTitle className="text-xl font-bold text-[#112d56]">GROW Model</CardTitle>
                    </div>
                    <CardDescription className="text-gray-600 text-base leading-relaxed">
                      <strong>Goal • Reality • Options • Will</strong><br/>
                      Building direction and structure through systematic goal achievement.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      Provides clear framework for setting objectives, assessing current reality, exploring possibilities, and committing to action.
                    </p>
                  </CardContent>
                </Card>

                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <div className="h-3 bg-gradient-to-r from-green-500 to-green-600"></div>
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-3">
                      <Eye className="h-6 w-6 text-green-500" />
                      <CardTitle className="text-xl font-bold text-[#112d56]">CLEAR Model</CardTitle>
                    </div>
                    <CardDescription className="text-gray-600 text-base leading-relaxed">
                      <strong>Contract • Listen • Explore • Action • Review</strong><br/>
                      Fostering trust and deep listening for authentic breakthroughs.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      Creates psychological safety and builds rapport essential for transformational coaching conversations.
                    </p>
                  </CardContent>
                </Card>

                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <div className="h-3 bg-gradient-to-r from-purple-500 to-purple-600"></div>
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-3">
                      <Star className="h-6 w-6 text-purple-500" />
                      <CardTitle className="text-xl font-bold text-[#112d56]">OSKAR Model</CardTitle>
                    </div>
                    <CardDescription className="text-gray-600 text-base leading-relaxed">
                      <strong>Outcome • Scaling • Know-how • Affirm • Review</strong><br/>
                      Spotlighting strengths and creating rapid wins through solution focus.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      Builds confidence by identifying existing strengths and leveraging them for accelerated progress.
                    </p>
                  </CardContent>
                </Card>

                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <div className="h-3 bg-gradient-to-r from-orange-500 to-orange-600"></div>
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-3">
                      <CheckCircle className="h-6 w-6 text-orange-500" />
                      <CardTitle className="text-xl font-bold text-[#112d56]">SMART Goals</CardTitle>
                    </div>
                    <CardDescription className="text-gray-600 text-base leading-relaxed">
                      <strong>Specific • Measurable • Achievable • Relevant • Time-bound</strong><br/>
                      Driving precision and accountability in goal setting.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      Ensures objectives are clear, measurable, and achievable within defined timeframes for maximum success.
                    </p>
                  </CardContent>
                </Card>

                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <div className="h-3 bg-gradient-to-r from-red-500 to-red-600"></div>
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-3">
                      <Brain className="h-6 w-6 text-red-500" />
                      <CardTitle className="text-xl font-bold text-[#112d56]">Emotional Intelligence</CardTitle>
                    </div>
                    <CardDescription className="text-gray-600 text-base leading-relaxed">
                      <strong>Self-Awareness • Empathy • Social Skills</strong><br/>
                      Deepening self-awareness and empathy for enhanced leadership impact.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      Develops emotional competencies essential for authentic leadership and meaningful relationships.
                    </p>
                  </CardContent>
                </Card>

                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <div className="h-3 bg-gradient-to-r from-indigo-500 to-indigo-600"></div>
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-3">
                      <Users className="h-6 w-6 text-indigo-500" />
                      <CardTitle className="text-xl font-bold text-[#112d56]">Transformational Leadership</CardTitle>
                    </div>
                    <CardDescription className="text-gray-600 text-base leading-relaxed">
                      <strong>Purpose • Vision • Inspiration</strong><br/>
                      Elevating purpose-driven impact and inspirational leadership.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      Cultivates visionary leadership that inspires teams and drives meaningful organizational change.
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Conclusion */}
              <div className="max-w-5xl mx-auto text-center">
                <Card className="border-0 shadow-xl bg-gradient-to-br from-[#112d56] to-[#1a3d6b] text-white overflow-hidden">
                  <CardContent className="p-10">
                    <h3 className="text-3xl font-bold mb-6">
                      Integrated AI-Powered <span className="text-[#ff7a1f]">Coaching Excellence</span>
                    </h3>
                    <p className="text-xl text-blue-100 leading-relaxed mb-6">
                      Together, these models empower managers and executives to lead with clarity, confidence, and authenticity — while supported by the intelligent guidance of AI.
                    </p>
                    <p className="text-lg text-blue-200 leading-relaxed mb-8">
                      VioCoach seamlessly weaves these methodologies into personalized coaching conversations, adapting to your unique leadership style, challenges, and aspirations. The result is a coaching experience that combines the best of human wisdom with the precision and availability of artificial intelligence.
                    </p>
                    
                    {/* Executive Models Addition */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mt-8">
                      <div className="flex items-center justify-center space-x-3 mb-6">
                        <Users className="h-8 w-8 text-[#ff7a1f]" />
                        <h4 className="text-2xl font-bold text-white">Advanced Executive Coaching Framework</h4>
                      </div>
                      <p className="text-lg text-blue-100 leading-relaxed mb-4">
                        Beyond these foundational six models, VioCoach integrates <span className="text-[#ff7a1f] font-bold">10 additional specialized executive coaching models</span> designed specifically for deeper and more focused C-suite and senior leadership development.
                      </p>
                      <p className="text-base text-blue-200 leading-relaxed">
                        These advanced methodologies address the unique challenges faced by executives, including strategic thinking, organizational transformation, stakeholder management, and high-stakes decision-making under pressure. This comprehensive 16-model framework ensures VioCoach delivers executive coaching that matches the sophistication and complexity required at the highest levels of leadership.
                      </p>
                      
                      <div className="mt-6 p-4 bg-[#ff7a1f]/20 rounded-lg border border-[#ff7a1f]/30">
                        <p className="text-sm text-blue-100 font-medium">
                          <strong>Total Coaching Framework:</strong> 6 Core Models + 10 Executive Models = 16 Integrated Methodologies
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Launch Status Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="border-0 shadow-2xl bg-gradient-to-br from-[#112d56] to-[#1a3d6b] text-white overflow-hidden">
              <CardContent className="p-6 sm:p-8 lg:p-12">
                <div className="space-y-6">
                  <div className="inline-flex items-center space-x-2 bg-[#ff7a1f]/20 backdrop-blur-sm rounded-full px-4 sm:px-6 py-3">
                    <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-[#ff7a1f]" />
                    <span className="text-[#ff7a1f] font-bold text-base sm:text-lg">LAUNCH STATUS</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold leading-tight">
                    VioCoach is currently in <span className="text-[#ff7a1f]">final development stages</span>.
                  </h2>
                  <p className="text-lg sm:text-xl text-blue-100 leading-relaxed max-w-2xl mx-auto px-4">
                    Launch date will be announced very soon. Be the first to know when VioCoach becomes available.
                  </p>
                  
                  <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-4 justify-center pt-6 max-w-md sm:max-w-2xl mx-auto">
                    <input
                      type="email"
                      placeholder="Enter your email for priority access"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1 px-4 sm:px-6 py-3 sm:py-4 rounded-xl text-[#112d56] placeholder-gray-500 font-medium text-base sm:text-lg min-w-0"
                      required
                      disabled={isSubmitting}
                    />
                    <Button 
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg rounded-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      <Mail className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                      {isSubmitting ? 'Signing Up...' : 'Get Launch Updates'}
                    </Button>
                  </form>
                  
                  {/* Feedback Message */}
                  {submitMessage && (
                    <div className={`mt-4 p-3 rounded-lg text-center max-w-md sm:max-w-2xl mx-auto ${
                      submitSuccess 
                        ? 'bg-green-100 border border-green-300 text-green-800' 
                        : 'bg-red-100 border border-red-300 text-red-800'
                    }`}>
                      {submitMessage}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight">
              The Future of Executive <span className="text-[#ff7a1f]">Coaching</span>
            </h2>
            <p className="text-lg sm:text-xl lg:text-2xl text-blue-100 leading-relaxed px-4">
              Be among the first to experience AI-powered leadership development with VioCoach.
            </p>
            
            <div className="flex justify-center pt-6 sm:pt-8">
              <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 w-full max-w-md sm:max-w-lg">
                <input
                  type="email"
                  placeholder="Enter your work email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-4 sm:px-6 py-3 rounded-xl text-[#112d56] placeholder-gray-500 font-medium min-w-0"
                  required
                  disabled={isSubmitting}
                />
                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-6 sm:px-8 py-3 rounded-xl transition-all duration-300 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </form>
            </div>
            
            {/* Feedback Message for Bottom Form */}
            {submitMessage && (
              <div className={`mt-4 p-3 rounded-lg text-center max-w-md sm:max-w-lg mx-auto ${
                submitSuccess 
                  ? 'bg-green-100/20 border border-green-300/30 text-green-100' 
                  : 'bg-red-100/20 border border-red-300/30 text-red-100'
              }`}>
                {submitMessage}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#112d56] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-8 h-8 bg-[#ff7a1f] rounded flex items-center justify-center">
                  <span className="text-white font-bold text-sm">V</span>
                </div>
                <span className="text-2xl font-bold">VioQbase</span>
              </div>
              <p className="text-blue-200 leading-relaxed mb-8 max-w-xs">
                Voice-powered AI knowledge base for enterprise teams. Get instant answers, eliminate delays.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Product</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/features" className="hover:text-[#ff7a1f] transition-colors text-lg">Features</a></li>
                <li><a href="/use-cases" className="hover:text-[#ff7a1f] transition-colors text-lg">Use Cases</a></li>
                <li><a href="/products/concierge" className="hover:text-[#ff7a1f] transition-colors text-lg">Smart Parrot Concierge</a></li>
                <li><a href="/products/viocoach" className="hover:text-[#ff7a1f] transition-colors text-lg">VioCoach</a></li>
                <li><a href="https://cal.com/concierge/vioqbase-demo" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff7a1f] transition-colors text-lg">Book Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Company</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/about" className="hover:text-[#ff7a1f] transition-colors text-lg">About</a></li>
                <li><a href="/contact" className="hover:text-[#ff7a1f] transition-colors text-lg">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Legal</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a></li>
                <li><a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <Separator className="bg-blue-800/50 my-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-blue-200 text-lg">
              © 2025 VioQbase. All rights reserved.
            </p>
            <div className="flex space-x-8 text-blue-200 mt-4 md:mt-0">
              <a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a>
              <a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a>
              <a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VioCoachPage;